"use client";

import * as React from "react";
import { X, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "loading";
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type: Toast["type"]) => string;
  removeToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = React.useCallback((message: string, type: Toast["type"]) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    // Defense in depth: `message` is typed as string, but call sites building
    // it from an `any`-typed API response (e.g. `res.json()`) can bypass that
    // at runtime. Rendering a non-string here would crash the whole app tree
    // ("Objects are not valid as a React child"), so coerce defensively.
    const safeMessage =
      typeof message === "string" ? message : "Une erreur est survenue";
    setToasts((prev) => [...prev, { id, message: safeMessage, type }]);
    if (type !== "loading") {
      setTimeout(() => removeToast(id), 4000);
    }
    return id;
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

function ToastContainer({ toasts, removeToast }: { toasts: Toast[]; removeToast: (id: string) => void }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={() => removeToast(toast.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border bg-background/95 backdrop-blur-sm p-4 shadow-lg animate-in slide-in-from-right-full duration-300",
        toast.type === "success" && "border-green-500/20 bg-green-50/95 dark:bg-green-950/95",
        toast.type === "error" && "border-red-500/20 bg-red-50/95 dark:bg-red-950/95"
      )}
    >
      {toast.type === "success" && <CheckCircle className="size-5 text-green-600 dark:text-green-400" />}
      {toast.type === "error" && <AlertCircle className="size-5 text-red-600 dark:text-red-400" />}
      {toast.type === "loading" && <Loader2 className="size-5 text-primary animate-spin" />}
      <span className="text-sm font-medium text-foreground">{toast.message}</span>
      {toast.type !== "loading" && (
        <button
          onClick={onDismiss}
          className="ml-auto text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}
